import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useTable, usePagination } from 'react-table';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import Modal from 'react-modal';
import { AiOutlineClose, AiOutlineEdit, AiOutlineEye } from 'react-icons/ai';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import SummaryApi from '../common';
import NotInterestedModal from '../components/NotInterestedModal';
import Select from 'react-select';

// ────────────────────────────────────────────────
// THEME
// ────────────────────────────────────────────────
const THEME = {
  primary: 'rgb(23, 44, 87)',
  primaryLight: 'rgba(23, 44, 87, 0.08)',
  actionBlue: '#2563EB',
  actionBlueHover: '#1D4ED8',
  actionYellow: '#D97706',
  actionYellowHover: '#B45309',
  actionGreen: '#10B981',
  actionGreenHover: '#059669',
  actionPurple: '#7C3AED',
  actionPurpleHover: '#6D28D9',
  actionRed: '#EF4444',
  actionRedHover: '#DC2626',
  disabledGray: '#D1D5DB',
};

const STICKY_ACCESSORS = ['edit', 'view'];

const STATUS_CHIP_CLASS = {
  'Most Interested': 'bg-green-100 text-green-700',
  'Interested': 'bg-yellow-100 text-yellow-700',
  'Not Interested': 'bg-red-100 text-red-700',
  'Call Back': 'bg-blue-100 text-blue-700',
};
const NEUTRAL_CHIP_CLASS = 'bg-gray-100 text-gray-600';

const selectStyles = {
  control: (base, state) => ({
    ...base,
    borderRadius: '0.5rem',
    borderColor: state.isFocused ? '#172C57' : '#D1D5DB',
    boxShadow: state.isFocused ? '0 0 0 2px #172C57' : 'none',
    '&:hover': { borderColor: state.isFocused ? '#172C57' : '#9CA3AF' },
  }),
};

// ─────────────────────────────────────────────────────────────
// Advanced Search Modal (unchanged)
// ─────────────────────────────────────────────────────────────
const AdvancedSearchModal = ({
  isOpen,
  onRequestClose,
  filteredStudents,
  searchDistrict,
  setSearchDistrict,
  searchCity,
  setSearchCity,
  searchCollege,
  setSearchCollege,
  setFilteredStudents,
  studentsData,
}) => {
  const collegeOptions = useMemo(() => {
    if (!searchCity) return [];
    return Array.from(new Set(
      filteredStudents
        .filter(student => searchCity && student.city?.toLowerCase() === searchCity.toLowerCase())
        .map(student => student.clg_name)
    )).filter(Boolean).map(college => ({ value: college, label: college }));
  }, [filteredStudents, searchCity]);

  return (
    <Modal
      isOpen={isOpen}
      contentLabel="Search Filters"
      className="mx-auto my-10 mt-[100px] w-full max-w-md bg-white rounded-lg shadow-lg"
      overlayClassName="fixed inset-0 bg-black bg-opacity-50 z-50"
    >
      <div className="sticky top-0 z-10 bg-white px-6 py-4 border-b border-slate-100">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-semibold" style={{ color: THEME.primary }}>
            Search Filters
          </h2>
          <button onClick={onRequestClose}>
            <AiOutlineClose className="w-6 h-6 text-gray-600 hover:text-gray-800" />
          </button>
        </div>
      </div>

      <div className="p-6 space-y-4">
        <div className="flex flex-col">
          <label htmlFor="district-filter" className="mb-1 font-semibold text-sm" style={{ color: THEME.primary }}>
            District
          </label>
          <select
            id="district-filter"
            className="p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#172C57] focus:border-[#172C57]"
            value={searchDistrict}
            onChange={(e) => {
              setSearchDistrict(e.target.value);
              setSearchCity('');
              setSearchCollege([]);
            }}
          >
            <option value="">Select District</option>
            {Array.from(new Set(filteredStudents.map(s => s.district))).filter(Boolean).map(d => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
        </div>

        <div className="flex flex-col">
          <label htmlFor="city-filter" className="mb-1 font-semibold text-sm" style={{ color: THEME.primary }}>
            City
          </label>
          <select
            id="city-filter"
            className="p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#172C57] focus:border-[#172C57] disabled:bg-gray-100"
            value={searchCity}
            onChange={(e) => { setSearchCity(e.target.value); setSearchCollege([]); }}
            disabled={!searchDistrict}
          >
            <option value="">{searchDistrict ? "Select City" : "First select District"}</option>
            {Array.from(new Set(
              filteredStudents
                .filter(s => searchDistrict && s.district?.toLowerCase() === searchDistrict.toLowerCase())
                .map(s => s.city)
            )).filter(Boolean).map(c => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>

        <div className="flex flex-col">
          <label htmlFor="college-filter" className="mb-1 font-semibold text-sm" style={{ color: THEME.primary }}>
            College Name
          </label>
          <Select
            isMulti
            options={collegeOptions}
            value={collegeOptions.filter(o => Array.isArray(searchCollege) && searchCollege.includes(o.value))}
            onChange={(opts) => setSearchCollege(opts.map(o => o.value))}
            isDisabled={!searchCity}
            placeholder={searchCity ? "Select College(s)" : "First select City"}
            styles={selectStyles}
          />
        </div>

        <div className="flex justify-end pt-4">
          <button
            onClick={() => {
              setSearchDistrict('');
              setSearchCity('');
              setSearchCollege([]);
              setFilteredStudents(studentsData);
            }}
            style={{ backgroundColor: THEME.actionRed }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = THEME.actionRedHover)}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = THEME.actionRed)}
            className="px-4 py-2 mr-2 text-white rounded-lg shadow-sm transition-colors"
          >
            Clear
          </button>
          <button
            onClick={onRequestClose}
            style={{ backgroundColor: THEME.primary }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.9')}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
            className="px-4 py-2 text-white rounded-lg shadow-sm transition-opacity"
          >
            Apply
          </button>
        </div>
      </div>
    </Modal>
  );
};

// ─────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────
const NotInterestedStudents = () => {
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [studentsData, setStudentsData] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [isUpdate, setIsUpdate] = useState(false);
  const [isUpload, setIsUpload] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [enrollmentCode, setEnrollmentCode] = useState(null);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [dropdownValue, setDropdownValue] = useState('');
  const [minEnrolledDate, setMinEnrolledDate] = useState(null);
  const [maxEnrolledDate, setMaxEnrolledDate] = useState(new Date());
  const [selectedRange, setSelectedRange] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('filter')?.toLowerCase() === 'today' ? 'today' : '';
  });
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [isCustomRange, setIsCustomRange] = useState(false);

  const [formData, setFormData] = useState({
    student_name: '', gender: 'Male', father_name: '', mobile_number1: '', mobile_number2: '',
    email: '', aadhar_number: '', state: '', district: '', city: '', address: '', pincode: '',
    course: '', clg_name: '', program_type: '', ug_pg_course: '', call_back_reason: '',
    call_duration: '00:00:00.000000', other_reasons: '', is_interested: '', call_status: '',
    remarks: '', prev_call_back_reason: ''
  });
  const [prevInterest, setPrevInterest] = useState('');
  const [memberType, setMemberType] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchModalIsOpen, setSearchModalIsOpen] = useState(false);
  const [searchDistrict, setSearchDistrict] = useState('');
  const [searchCity, setSearchCity] = useState('');
  const [searchCollege, setSearchCollege] = useState([]);
  const [canOnlySelectHigherInterest, setCanOnlySelectHigherInterest] = useState(false);
  const tableRef = useRef(null);
  const [latestFollowUpId, setLatestFollowUpId] = useState(null);
  const [shouldAutoScroll, setShouldAutoScroll] = useState(false);

  const districts = [
    'Alluri Sitharama Raju', 'Anakapalli', 'Ananthapuram', 'Annamayya', 'Bapatla', 'CHITTOOR',
    'EAST GODAVARI', 'Eluru', 'Guntur', 'Kakinada', 'Konaseema', 'Krishna', 'Kurnool',
    'Manyam', 'Nandyal', 'NTR', 'Palnadu', 'Prakasam', 'Srikakulam', 'SPS Nellore',
    'Sri Satyasai', 'Srikakulam', 'Tirupathi', 'Vizianagaram', 'Visakhapatnam',
    'West Godavari', 'YSR Kadapa'
  ];

  const headers = {
    'Authorization': 'Bearer sandeep_university_916',
    'Content-Type': 'application/json',
  };

  // ── FIX #1: pull filtered-state refs so filterStudents sees fresh values
  const filterStateRef = useRef({ selectedFilter: 'All', searchQuery: '', selectedRange: '' });
  useEffect(() => {
    filterStateRef.current = { selectedFilter, searchQuery, selectedRange };
  }, [selectedFilter, searchQuery, selectedRange]);

  // ── FIX #2: pure filtering function — no default params reading stale closure
  const applyFilters = useCallback((data, filter, search, range) => {
    let filtered = data || [];
    const timeZone = 'Asia/Kolkata';
    const formatToKolkata = (date) =>
      new Intl.DateTimeFormat('en-IN', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(date);

    if (filter && filter !== 'All') {
      if (filter === "JEE Mains") {
        filtered = filtered.filter(s => s.is_jee === "Jee Mains Results");
      } else if (filter === "JEE Mains2") {
        filtered = filtered.filter(s => s.is_jee2 === "Jee Mains-2 Results");
      } else if (filter === "Not JEE Mains") {
        filtered = filtered.filter(s =>
          s.is_jee !== "Jee Mains Results" && s.is_jee2 !== "Jee Mains-2 Results"
        );
      }
    }

    if (searchDistrict) {
      filtered = filtered.filter(s => s.district?.toLowerCase().includes(searchDistrict.toLowerCase()));
    }
    if (searchCity) {
      filtered = filtered.filter(s => s.city?.toLowerCase().includes(searchCity.toLowerCase()));
    }
    if (Array.isArray(searchCollege) && searchCollege.length > 0) {
      filtered = filtered.filter(s =>
        searchCollege.some(c => s.clg_name?.toLowerCase() === c.toLowerCase())
      );
    }

    const toKolkataDate = (d) => formatToKolkata(new Date(d));

    if (range === 'today') {
      const todayStr = formatToKolkata(new Date());
      filtered = filtered.filter(s => toKolkataDate(s.created_date) === todayStr);
    } else if (range === 'yesterday') {
      const y = new Date(); y.setDate(y.getDate() - 1);
      const yStr = formatToKolkata(y);
      filtered = filtered.filter(s => toKolkataDate(s.created_date) === yStr);
    } else if (range === 'thisWeek') {
      const today = new Date();
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - today.getDay());
      startOfWeek.setHours(0, 0, 0, 0);
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      endOfWeek.setHours(23, 59, 59, 999);
      filtered = filtered.filter(s => {
        const d = new Date(s.created_date);
        return d >= startOfWeek && d <= endOfWeek;
      });
    } else if (range === 'lastWeek') {
      const today = new Date();
      const startOfCurrentWeek = new Date(today);
      startOfCurrentWeek.setDate(today.getDate() - today.getDay());
      startOfCurrentWeek.setHours(0, 0, 0, 0);
      const endOfLastWeek = new Date(startOfCurrentWeek);
      endOfLastWeek.setDate(startOfCurrentWeek.getDate() - 1);
      endOfLastWeek.setHours(23, 59, 59, 999);
      const startOfLastWeek = new Date(endOfLastWeek);
      startOfLastWeek.setDate(endOfLastWeek.getDate() - 6);
      startOfLastWeek.setHours(0, 0, 0, 0);
      filtered = filtered.filter(s => {
        const d = new Date(s.created_date);
        return d >= startOfLastWeek && d <= endOfLastWeek;
      });
    } else if (range === 'custom') {
      if (fromDate && toDate) {
        const fromF = new Date(fromDate); fromF.setHours(0, 0, 0, 0);
        const toF = new Date(toDate); toF.setHours(23, 59, 59, 999);
        filtered = filtered.filter(s => {
          const d = new Date(s.created_date);
          return d >= fromF && d <= toF;
        });
      }
    }

    if (['Admin', 'Manager'].includes(memberType) && search) {
      filtered = filtered.filter(s =>
        s.student_name?.toLowerCase().includes(search.toLowerCase()) ||
        s.employee_name?.toLowerCase().includes(search.toLowerCase()) ||
        s.mobile_number1?.toLowerCase().includes(search.toLowerCase())
      );
    } else if (search) {
      filtered = filtered.filter(s =>
        s.student_name?.toLowerCase().includes(search.toLowerCase()) ||
        s.mobile_number1?.toLowerCase().includes(search.toLowerCase())
      );
    }

    return filtered;
  }, [searchDistrict, searchCity, searchCollege, fromDate, toDate, memberType]);

  // ── FIX #3: fetchStudents accepts optional fresh override and busts cache
  const fetchStudents = useCallback(async () => {
    setLoading(true);
    try {
      const encodedMemberType = sessionStorage.getItem('K3M8X1JQ5VZ4B7PWTL9');
      const decodedMemberType = encodedMemberType ? atob(encodedMemberType) : null;
      setMemberType(decodedMemberType);

      let response;
      if (decodedMemberType === 'Admin' || decodedMemberType === 'Manager') {
        response = await fetch(`${SummaryApi.getAllNotInterestedStudents.url}?t=${Date.now()}`, {
          method: SummaryApi.getAllNotInterestedStudents.method,
          headers,
          cache: 'no-store',
        });
      } else {
        const encodedEmployeeId = sessionStorage.getItem('H7Z4L9VD2XK5JW8QRYT6');
        if (!encodedEmployeeId) throw new Error('Employee ID not found in session storage');
        const decodedEmployeeId = atob(encodedEmployeeId);
        response = await fetch(`${SummaryApi.notInterestedStudents.url}?t=${Date.now()}`, {
          method: SummaryApi.notInterestedStudents.method,
          headers,
          cache: 'no-store',
          body: JSON.stringify({ employee_id: decodedEmployeeId }),
        });
      }

      if (!response.ok) throw new Error('Failed to fetch data from the endpoint');
      const data = await response.json();
      const students = data?.data || [];

      const updatedStudentsData = students.map((student, index) => ({ ...student, sno: index + 1 }));

      // ── FIX #4: apply filters FIRST so filteredStudents is refreshed atomically
      const refreshedFiltered = applyFilters(
        updatedStudentsData,
        filterStateRef.current.selectedFilter,
        filterStateRef.current.searchQuery,
        filterStateRef.current.selectedRange
      );

      setStudentsData(updatedStudentsData);
      setFilteredStudents(refreshedFiltered);

      // ── FIX #5: recompute latest follow-up from ALL data (not just filtered)
      let latestIndex = -1;
      let latestDate = null;
      updatedStudentsData.forEach((student, index) => {
        const fud = student.follow_up_date?.trim();
        if (!fud || fud === "0000-00-00 00:00:00" || fud === "N/A") return;
        const dateObj = new Date(fud);
        if (isNaN(dateObj.getTime())) return;
        if (!latestDate || dateObj > latestDate) { latestDate = dateObj; latestIndex = index; }
      });

      if (latestIndex !== -1) {
        setLatestFollowUpId(updatedStudentsData[latestIndex].student_id);
        setShouldAutoScroll(true);
      } else {
        setLatestFollowUpId(null);
        setShouldAutoScroll(false);
      }

      return updatedStudentsData; // return for callers who need the fresh data
    } catch (error) {
      toast.error(`Error fetching students: ${error.message}`);
      return null;
    } finally {
      setLoading(false);
    }
  }, [applyFilters]);

  useEffect(() => { fetchStudents(); }, [fetchStudents]);

  // ── Re-apply filters when any filter changes (studentsData included)
  useEffect(() => {
    if (studentsData.length === 0) return;
    const next = applyFilters(studentsData, selectedFilter, searchQuery, selectedRange);
    setFilteredStudents(next);
  }, [studentsData, selectedFilter, searchQuery, selectedRange,
    searchDistrict, searchCity, searchCollege, applyFilters]);

  const handleFilterChange = (filter) => setSelectedFilter(filter);

  useEffect(() => {
    if (studentsData?.length) {
      const dates = studentsData.map(s => new Date(s.created_date));
      setMinEnrolledDate(new Date(Math.min(...dates)));
      setMaxEnrolledDate(new Date());
    }
  }, [studentsData]);

  const handleRangeChange = (e) => {
    const range = e.target.value;
    setSelectedRange(range);
    setIsCustomRange(range === 'custom');
    if (range !== 'custom') { setFromDate(null); setToDate(null); }
  };

  const handleSearchChange = (e) => setSearchQuery(e.target.value);

  const ugCourses = ['CSE', 'CSE(AI & ML)', 'CSE(AI & DS)', 'AERONAUTICAL ENGINERRING', 'ECE'];
  const pgCourses = [];
  const courses = formData.program_type === 'UG' ? ugCourses : formData.program_type === 'PG' ? pgCourses : [];

  // ── openModal — unchanged logic, kept for completeness
  const openModal = (student = null, mode = 'view') => {
    const sanitizeStudentData = (data) => {
      const sanitizedData = { ...data };
      Object.keys(sanitizedData).forEach((key) => {
        if (sanitizedData[key] === "N/A" || sanitizedData[key] === "n/a") sanitizedData[key] = "";
      });
      return sanitizedData;
    };

    const reasonsForOthers = [
      "Wrong Number", "Not Reachable", "Number Not Working", "Call Declined", "Busy",
      "Switched Off", "Incoming Calls Stopped"
    ];

    const payload = {
      employee_id: student?.employee_id,
      student_name: student?.student_name,
      gender: student?.gender,
      father_name: student?.father_name,
      mobile_number1: student?.mobile_number1,
      mobile_number2: student?.mobile_number2 || '',
      email: student?.email || '',
      aadhar_number: student?.aadhar_number || '',
      state: student?.state,
      district: student?.district,
      city: student?.city,
      address: student?.address,
      pincode: student?.pincode || '',
      course: student?.course,
      clg_name: student?.clg_name,
      inter_percentage: student?.inter_percentage || '',
      program_type: student?.program_type,
      ug_pg_course: student?.ug_pg_course,
      call_status: student?.call_status || '',
      call_duration: student?.call_duration || '00:00:00.000000',
      remarks: student?.remarks || '',
      is_interested: reasonsForOthers.includes(student?.is_interested) ? "Others" : student?.is_interested,
      other_reasons: reasonsForOthers.includes(student?.is_interested) ? student?.is_interested : '',
      call_back_reason: student?.call_back_reason,
      prev_call_back_reason: student?.call_back_reason,
      is_jee: student?.is_jee,
      is_jee2: student?.is_jee2,
    };

    const sanitizedStudent = sanitizeStudentData(payload);
    setPrevInterest(sanitizedStudent.is_interested || 'N/A');

    if (mode === 'edit' && sanitizedStudent.is_interested === 'Not Interested') {
      setCanOnlySelectHigherInterest(true);
    } else {
      setCanOnlySelectHigherInterest(false);
    }

    const [timePart = '00:00:00'] = sanitizedStudent.call_duration.split('.');
    const [hours, minutes, seconds] = timePart.split(':');

    setFormData({
      ...sanitizedStudent,
      hours: hours?.padStart(2, '0') || '00',
      minutes: minutes?.padStart(2, '0') || '00',
      seconds: seconds?.padStart(2, '0') || '00',
    });

    if (mode === 'edit') {
      setIsUpdate(true); setIsUpload(false);
      const coursesArray = sanitizedStudent.ug_pg_course
        ? sanitizedStudent.ug_pg_course.split(',').map(c => c.trim()) : [];
      setSelectedCourses(coursesArray);
    } else if (mode === 'add') {
      setFormData({
        student_name: '', gender: 'Male', father_name: '', mobile_number1: '', mobile_number2: '',
        email: '', aadhar_number: '', state: '', district: '', city: '', address: '', pincode: '',
        course: '', clg_name: '', remarks: '', call_status: '', call_duration: '00:00:00.000000',
        program_type: '', ug_pg_course: '', is_interested: '', inter_percentage: '', is_jee: '', is_jee2: '',
        hours: '00', minutes: '00', seconds: '00', other_reasons: '', call_back_reason: ''
      });
      setIsUpdate(false); setIsUpload(true);
    } else {
      const coursesArray = sanitizedStudent.ug_pg_course
        ? sanitizedStudent.ug_pg_course.split(',').map(c => c.trim()) : [];
      setSelectedCourses(coursesArray);
    }

    setSelectedStudent(student);
    setModalIsOpen(true);
  };

  const handleAllClick = () => {
    setSelectedFilter('All'); setSearchQuery(''); setSelectedRange('');
    setFromDate(null); setToDate(null); setIsCustomRange(false);
    setSearchDistrict(''); setSearchCity(''); setSearchCollege([]);
    setFilteredStudents(studentsData);
  };

  const closeModal = () => {
    setModalIsOpen(false); setSelectedStudent(null);
    setIsUpdate(false); setIsUpload(false);
    setSelectedCourses([]); setDropdownValue('');
    setCanOnlySelectHigherInterest(false);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'call_status' && (value === 'Not Answered' || value === '')) {
      setFormData(prev => ({ ...prev, is_interested: '', [name]: value }));
    } else if (name === 'is_interested') {
      setFormData(prev => ({
        ...prev, [name]: value,
        ...(value !== 'Call Back' && { call_back_reason: '' }),
        ...(value !== 'Others' && { other_reasons: '' }),
      }));
    } else if (name === 'program_type' && (value === 'PG' || value === '')) {
      setSelectedCourses([]); setDropdownValue('');
      setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    } else {
      setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    }
  };

  const handleSelectChange = (e) => {
    const selectedCourse = e.target.value;
    if (selectedCourse && !selectedCourses.includes(selectedCourse)) {
      setSelectedCourses(prev => [...prev, selectedCourse]);
    }
    setDropdownValue('');
  };

  const handleRemoveTag = (course) => {
    setSelectedCourses(prev => prev.filter(c => c !== course));
  };

  useEffect(() => {
    if (formData.call_duration) {
      const [timePart] = formData.call_duration.split('.');
      const [h, m, s] = timePart.split(':').map(p => p.padStart(2, '0'));
      setFormData(prev => ({ ...prev, hours: h, minutes: m, seconds: s }));
    }
  }, [formData.call_duration]);

  // ── FIX #6: submit — waits for refetch, resets pagination, forces fresh filtered state
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoadingSubmit(true);
    try {
      const encodedEmployeeId = sessionStorage.getItem('H7Z4L9VD2XK5JW8QRYT6');
      if (!encodedEmployeeId) throw new Error('Employee ID not found in session storage');
      const decodedEmployeeId = atob(encodedEmployeeId);

      const apiEndpoint = isUpdate ? SummaryApi.updateStudent.url : SummaryApi.uploadStudent.url;
      const method = isUpdate ? SummaryApi.updateStudent.method : SummaryApi.uploadStudent.method;

      const payloadData = {
        employee_student_id: formData.employee_student_id,
        student_name: formData.student_name,
        last_name: '',
        gender: formData.gender,
        father_name: formData.father_name,
        mobile_number1: formData.mobile_number1,
        mobile_number2: formData.mobile_number2 || '',
        email: formData.email || '',
        aadhar_number: formData.aadhar_number || '',
        state: formData.state,
        district: formData.district,
        city: formData.city,
        address: formData.address,
        pincode: formData.pincode || '',
        course: formData.course,
        clg_name: formData.clg_name,
        inter_percentage: formData.inter_percentage || '',
        program_type: formData.program_type,
        ug_pg_course: selectedCourses.join(', '),
        call_status: formData.call_status || '',
        call_duration: formData.call_duration || '00:00:00.000000',
        remarks: formData.remarks || '',
        is_interested: formData.is_interested === 'Others' ? formData.other_reasons : formData.is_interested,
        prevs_is_interested: formData.is_interested === 'Not Interested'
          ? 'Not Interested'
          : (isUpdate ? prevInterest : ''),
        is_jee: (formData.is_jee === "Jee Mains Results")
          ? "Jee Mains Results"
          : (["Interested", "Not Interested", "Most Interested"].includes(formData.is_interested))
            ? (formData.prev_call_back_reason === "Jee Mains Results" ? "Jee Mains Results" : "")
            : "",
        is_jee2: (formData.is_jee2 === "Jee Mains-2 Results")
          ? "Jee Mains-2 Results"
          : (["Interested", "Not Interested", "Most Interested"].includes(formData.is_interested))
            ? (formData.prev_call_back_reason === "Jee Mains-2 Results" ? "Jee Mains-2 Results" : "")
            : "",
        call_back_reason: formData.call_back_reason || '',
      };

      // ── FIX #7: preserve the ORIGINAL employee_id from the student row so the
      // employee-scoped backend can still locate the record. Admins still send
      // the logged-in employee id; employees send the row's original owner.
      const originalEmployeeId = selectedStudent?.employee_id;
      const targetEmployeeId = ['Admin', 'Manager'].includes(memberType)
        ? decodedEmployeeId
        : (originalEmployeeId || decodedEmployeeId);

      const payload = isUpdate
        ? { ...payloadData, student_id: selectedStudent.student_id, employee_id: targetEmployeeId }
        : { ...payloadData, employee_id: decodedEmployeeId };

      const response = await fetch(apiEndpoint, {
        method,
        headers,
        cache: 'no-store',
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error('Network response was not ok');
      const result = await response.json();

      if (result.status) {
        toast.success(isUpdate ? 'Student updated successfully!' : 'Student added successfully!');
        if (!isUpdate) setEnrollmentCode(result.ENR_CODE);

        closeModal();

        // ── FIX #8: await the refetch AND force filter reapplication on returned data
        const freshData = await fetchStudents();

        if (freshData) {
          const nextFiltered = applyFilters(
            freshData,
            filterStateRef.current.selectedFilter,
            filterStateRef.current.searchQuery,
            filterStateRef.current.selectedRange
          );
          setFilteredStudents(nextFiltered);
        }

        // ── FIX #9: reset to page 0 so the updated row is visible
        gotoPage(0);
        setShouldAutoScroll(true);
      } else {
        toast.error(result.message || 'Update failed');
      }
    } catch (error) {
      toast.error(`Error: ${error.message}`);
    } finally {
      setLoadingSubmit(false);
    }
  };

  const downloadExcelReport = () => {
    const requiredFields = [
      'enrollment_code', 'student_name', 'email', 'mobile_number1', 'mobile_number2',
      'aadhar_number', 'state', 'district', 'city', 'pincode', 'course', 'clg_name',
      'created_date', 'call_status', 'call_duration', 'remarks', 'employee_name'
    ];
    const filteredData = studentsData.map(student => {
      const selectedData = {};
      requiredFields.forEach(field => { selectedData[field] = student[field]; });
      return selectedData;
    });

    const headersMap = {
      enrollment_code: 'Enrollment Code', student_name: 'Student Name', email: 'Email',
      mobile_number1: 'Mobile Number 1', mobile_number2: 'Mobile Number 2',
      aadhar_number: 'Aadhar Number', state: 'State', district: 'District', city: 'City',
      address: 'Address', pincode: 'Pincode', course: 'Course', clg_name: 'College',
      created_date: 'Enrolled Date', call_status: 'Call Status', call_duration: 'Call Duration',
      remarks: 'Remarks', employee_name: 'Employee Name'
    };

    const formattedData = filteredData.map(student =>
      Object.fromEntries(Object.entries(student).map(([k, v]) => [headersMap[k] || k, v]))
    );

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Students');
    XLSX.writeFile(workbook, 'not_interested_students_report.xlsx');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    if (isNaN(date)) return "NA";
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const columns = useMemo(() => [
    { Header: 'SNO', Cell: ({ row }) => row.index + 1 },
    ...(['Admin', 'Manager'].includes(memberType) ? [{ Header: 'Employee Name', accessor: 'employee_name' }] : []),
    { Header: 'Student Name', accessor: 'student_name' },
    { Header: 'District', accessor: 'district' },
    { Header: 'City', accessor: 'city' },
    { Header: 'College Name', accessor: 'clg_name' },
    {
      Header: "Address",
      accessor: "address",
      Cell: ({ value }) => {
        if (!value || value === "N/A" || value === "n/a") {
          return <span className="text-gray-400">NA</span>;
        }
        return (
          <div className="group relative max-w-[100px]">
            <span className="block truncate text-gray-700">{value}</span>
            <div className="absolute z-50 hidden group-hover:block bg-black text-white text-xs rounded px-2 py-1 left-0 top-6 shadow-lg w-max max-w-[300px] whitespace-normal break-words pointer-events-none">
              {value}
            </div>
          </div>
        );
      },
    },
    { Header: 'Mobile Number', accessor: 'mobile_number1' },
    {
      Header: 'Call Duration (HH:MM:SS)', accessor: 'call_duration',
      Cell: ({ value }) => {
        if (value === "n/a") return value;
        if (value.includes(':')) {
          const [timePart] = value.split('.');
          const [h, m, s] = timePart.split(':');
          return `${h.padStart(2, '0')}:${m.padStart(2, '0')}:${s.padStart(2, '0')}`;
        }
        return value;
      },
    },
    { Header: 'Course', accessor: 'course' },
    {
      Header: 'Enrolled Date', accessor: 'enroll_date',
      Cell: ({ row }) => <span>{formatDate(row.original.created_date)}</span>,
    },
    {
      Header: 'Follow Up Date', accessor: 'follow_up_date',
      Cell: ({ row }) => <span>{formatDate(row.original.follow_up_date)}</span>,
    },
    {
      Header: 'Previous Status', accessor: 'prevs_is_interested',
      Cell: ({ value }) => {
        if (!value) value = 'NA';
        const chipClass = STATUS_CHIP_CLASS[value] || NEUTRAL_CHIP_CLASS;
        return <span className={`inline-flex items-center whitespace-nowrap px-2.5 py-0.5 rounded-full text-xs font-semibold ${chipClass}`}>{value}</span>;
      },
    },
    {
      Header: 'Current Status', accessor: 'is_interested',
      Cell: ({ value }) => {
        const chipClass = STATUS_CHIP_CLASS[value] || NEUTRAL_CHIP_CLASS;
        return <span className={`inline-flex items-center whitespace-nowrap px-2.5 py-0.5 rounded-full text-xs font-semibold ${chipClass}`}>{value}</span>;
      },
    },
    { Header: 'Jee Mains-1', accessor: 'is_jee' },
    { Header: 'Jee Mains-2', accessor: 'is_jee2' },
    ...(['Employee', 'Consultant', 'Team Lead'].includes(memberType) ? [{
      Header: 'Edit', accessor: 'edit',
      Cell: ({ row }) => (
        <button
          style={{ backgroundColor: THEME.actionBlue }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = THEME.actionBlueHover)}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = THEME.actionBlue)}
          className="text-white px-3 py-2 rounded-md transition-colors shadow-sm inline-flex items-center gap-1.5 whitespace-nowrap"
          onClick={() => openModal(row.original, 'edit')}
        >
          <AiOutlineEdit className="shrink-0" /> Edit
        </button>
      ),
    }] : []),
    {
      Header: 'View More', accessor: 'view',
      Cell: ({ row }) => (
        <button
          style={{ backgroundColor: THEME.actionYellow }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = THEME.actionYellowHover)}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = THEME.actionYellow)}
          className="text-white px-3 py-2 rounded-md transition-colors shadow-sm inline-flex items-center gap-1.5 whitespace-nowrap"
          onClick={() => openModal(row.original, 'view')}
        >
          <AiOutlineEye className="shrink-0" /> View More
        </button>
      ),
    },
  ], [memberType]);

  const {
    getTableProps, getTableBodyProps, headerGroups, prepareRow, page,
    canPreviousPage, canNextPage, pageCount, gotoPage, nextPage, previousPage, setPageSize,
    state: { pageIndex, pageSize },
  } = useTable(
    { columns, data: filteredStudents, initialState: { pageIndex: 0 } },
    usePagination
  );

  const getRowClass = (student) => {
    let base = "transition";
    const fud = student.follow_up_date ? new Date(student.follow_up_date) : null;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    let isPastOrToday = false;
    if (fud) { fud.setHours(0, 0, 0, 0); isPastOrToday = fud.getTime() <= today.getTime(); }
    const isLatest = student.student_id === latestFollowUpId;
    if (isLatest) base += " bg-yellow-200 font-semibold";
    else if (isPastOrToday) base += " bg-green-100 font-semibold";
    else base += " hover:bg-slate-50";
    return base;
  };

  const getStickyCellBg = (student) => {
    const isLatest = student.student_id === latestFollowUpId;
    const fud = student.follow_up_date ? new Date(student.follow_up_date) : null;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    let isPastOrToday = false;
    if (fud) { fud.setHours(0, 0, 0, 0); isPastOrToday = fud.getTime() <= today.getTime(); }
    if (isLatest) return '#FEF08A';
    if (isPastOrToday) return '#DCFCE7';
    return '#FFFFFF';
  };

  useEffect(() => {
    if (!shouldAutoScroll || !latestFollowUpId || !tableRef.current) return;
    const scrollAndHighlightLatest = () => {
      if (!tableRef.current) return;
      const row = tableRef.current.querySelector(`tr[data-student-id="${latestFollowUpId}"]`);
      if (row) {
        row.scrollIntoView({ behavior: "smooth", block: "center" });
        setShouldAutoScroll(false);
      }
    };
    const studentIndex = filteredStudents.findIndex(s => s.student_id === latestFollowUpId);
    if (studentIndex === -1) { setShouldAutoScroll(false); return; }
    const targetPage = Math.floor(studentIndex / pageSize);
    if (pageIndex !== targetPage) {
      gotoPage(targetPage);
      setTimeout(scrollAndHighlightLatest, 600);
    } else {
      setTimeout(scrollAndHighlightLatest, 200);
    }
  }, [shouldAutoScroll, latestFollowUpId, filteredStudents, pageIndex, pageSize, gotoPage]);

  const clearFilters = () => {
    setSelectedRange(''); setFromDate(null); setToDate(null); setIsCustomRange(false);
    setSearchQuery(''); setSelectedFilter('All');
    setSearchDistrict(''); setSearchCity(''); setSearchCollege([]);
    setFilteredStudents(studentsData);
    const url = new URL(window.location.href);
    url.searchParams.delete('filter');
    window.history.replaceState({}, '', url);
  };

  const pageBtnClass =
    "px-3 py-1 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed";

  return (
    <div className="min-h-screen w-full">
      {loading ? (
        <div className="flex justify-center items-center py-24">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2" style={{ borderColor: THEME.primary }} />
        </div>
      ) : (
        <>
          <div className="w-full space-y-5">
            {loadingSubmit && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
                <div className="bg-white rounded-xl shadow-sm px-6 py-4 flex items-center gap-3">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2" style={{ borderColor: THEME.primary }} />
                  <span className="text-sm font-semibold" style={{ color: THEME.primary }}>Submitting...</span>
                </div>
              </div>
            )}

            <div className='flex justify-between items-center'>
              <h1 className="text-2xl md:text-3xl font-bold" style={{ color: THEME.primary }}>
                Not Interested Students
              </h1>
              <div className="flex flex-row gap-2 items-center">
                <button
                  onClick={() => setSearchModalIsOpen(true)}
                  style={{ backgroundColor: THEME.primary }}
                  onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.9')}
                  onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
                  className="text-white px-4 py-2 rounded-lg flex items-center shadow-sm transition-opacity"
                >
                  Advanced Search
                </button>
                <button
                  style={{ backgroundColor: THEME.actionGreen }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = THEME.actionGreenHover)}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = THEME.actionGreen)}
                  className="text-white px-4 py-2 rounded-lg shadow-sm transition-colors"
                  onClick={downloadExcelReport}
                >
                  Download Excel Report
                </button>
              </div>
            </div>

            <div className='flex flex-wrap justify-between items-center gap-4 bg-white rounded-xl border border-slate-200 shadow-sm p-4'>
              <div className="inline-flex items-center gap-2 bg-gray-100 p-1 rounded-lg">
                {[
                  { key: 'All', label: 'All', onClick: handleAllClick },
                  { key: 'JEE Mains', label: 'JEE Mains-1' },
                  { key: 'JEE Mains2', label: 'JEE Mains-2' },
                  { key: 'Not JEE Mains', label: 'Non-JEE Mains' },
                ].map(({ key, label, onClick }) => (
                  <button
                    key={key}
                    className={`py-2 px-4 rounded-lg text-sm font-semibold transition ${selectedFilter === key ? 'text-white shadow' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                    style={selectedFilter === key ? { backgroundColor: THEME.primary } : {}}
                    onClick={onClick || (() => handleFilterChange(key))}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {['Admin', 'Manager'].includes(memberType) && (
                <div>
                  <div className="flex items-center">
                    <label htmlFor="dateRange" className="text-sm font-semibold" style={{ color: THEME.primary }}>
                      Select Date Range:
                    </label>
                    <select
                      id="dateRange"
                      style={{ color: THEME.primary }}
                      className='py-2 px-4 ml-4 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#172C57] focus:border-[#172C57]'
                      onChange={handleRangeChange}
                      value={selectedRange}
                    >
                      <option value="">Select a range</option>
                      <option value="today">Today</option>
                      <option value="yesterday">Yesterday</option>
                      <option value="thisWeek">This Week</option>
                      <option value="lastWeek">Last Week</option>
                      <option value="custom">Custom</option>
                    </select>
                    <button
                      onClick={clearFilters}
                      style={{ backgroundColor: THEME.actionRed }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = THEME.actionRedHover)}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = THEME.actionRed)}
                      className="px-4 py-2 ml-4 text-white text-sm font-semibold rounded-lg shadow-sm transition-colors focus:outline-none"
                    >
                      Clear Filters
                    </button>
                  </div>

                  {isCustomRange && (
                    <div className='flex mt-4 gap-4'>
                      <div>
                        <label className="text-sm font-semibold" style={{ color: THEME.primary }}>From:</label>
                        <DatePicker
                          selected={fromDate}
                          onChange={(date) => setFromDate(date)}
                          selectsStart
                          startDate={fromDate}
                          endDate={toDate}
                          minDate={minEnrolledDate}
                          maxDate={toDate || maxEnrolledDate}
                          dateFormat="dd-MM-yyyy"
                          placeholderText="Select From Date"
                          className='p-2 ml-2 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#172C57] focus:border-[#172C57]'
                        />
                      </div>
                      <div>
                        <label className="text-sm font-semibold" style={{ color: THEME.primary }}>To:</label>
                        <DatePicker
                          selected={toDate}
                          onChange={(date) => setToDate(date)}
                          selectsEnd
                          startDate={fromDate}
                          endDate={toDate}
                          minDate={fromDate || minEnrolledDate}
                          maxDate={maxEnrolledDate}
                          dateFormat="dd-MM-yyyy"
                          placeholderText="Select To Date"
                          className='p-2 ml-2 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#172C57] focus:border-[#172C57] disabled:bg-gray-100'
                          disabled={!fromDate}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className='w-full md:w-1/3'>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  placeholder={['Admin', 'Manager'].includes(memberType)
                    ? "Search by student name or mobile number or employee name"
                    : "Search by student name or mobile number"}
                  className="py-2 px-4 border border-gray-300 rounded-lg w-full text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#172C57] focus:border-[#172C57]"
                />
              </div>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
              <div ref={tableRef} className="overflow-x-auto">
                <table {...getTableProps()} key={filteredStudents.length} className="min-w-full mb-4 rounded-lg overflow-hidden text-sm text-gray-700">
                  <thead className="bg-gray-200">
                    {headerGroups.map(headerGroup => (
                      <tr {...headerGroup.getHeaderGroupProps()}>
                        {headerGroup.headers.map(column => {
                          const isSticky = STICKY_ACCESSORS.includes(column.id) || STICKY_ACCESSORS.includes(column.accessor);
                          return (
                            <th
                              {...column.getHeaderProps()}
                              className={`px-4 py-2 text-left font-semibold whitespace-nowrap ${isSticky ? 'sticky right-0 z-20 bg-gray-200 shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.15)]' : ''}`}
                              style={{ color: THEME.primary }}
                            >
                              {column.render('Header')}
                            </th>
                          );
                        })}
                      </tr>
                    ))}
                  </thead>
                  <tbody {...getTableBodyProps()}>
                    {page.map(row => {
                      prepareRow(row);
                      const student = row.original;
                      const rowClass = getRowClass(student);
                      const stickyBg = getStickyCellBg(student);

                      const stickyColsInOrder = row.cells.filter(cell =>
                        STICKY_ACCESSORS.includes(cell.column.id) || STICKY_ACCESSORS.includes(cell.column.accessor)
                      );

                      return (
                        <tr {...row.getRowProps()} data-student-id={student.student_id} className={rowClass}>
                          {row.cells.map(cell => {
                            const isSticky = STICKY_ACCESSORS.includes(cell.column.id) || STICKY_ACCESSORS.includes(cell.column.accessor);
                            if (!isSticky) {
                              return (
                                <td {...cell.getCellProps()} className="border border-slate-100 px-4 py-2">
                                  {cell.render('Cell')}
                                </td>
                              );
                            }

                            const stickyIndexInRow = stickyColsInOrder.indexOf(cell);
                            const totalSticky = stickyColsInOrder.length;
                            const stickyWidths = stickyColsInOrder.map(c =>
                              (c.column.accessor === 'edit' || c.column.id === 'edit') ? 120 : 150
                            );
                            let rightOffset = 0;
                            for (let i = stickyIndexInRow + 1; i < totalSticky; i++) {
                              rightOffset += stickyWidths[i];
                            }

                            return (
                              <td
                                {...cell.getCellProps()}
                                className="border border-slate-100 px-4 py-2 sticky z-10 shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.15)]"
                                style={{
                                  right: `${rightOffset}px`,
                                  backgroundColor: stickyBg,
                                  minWidth: `${stickyWidths[stickyIndexInRow]}px`,
                                  width: `${stickyWidths[stickyIndexInRow]}px`,
                                }}
                              >
                                {cell.render('Cell')}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-between items-center">
                <span style={{ color: THEME.primary }} className="text-sm font-medium">
                  Total records: <span className="font-semibold">{filteredStudents.length}</span>
                </span>
                <div className="flex items-center gap-1">
                  <button onClick={() => gotoPage(0)} disabled={!canPreviousPage} style={canPreviousPage ? { color: THEME.primary } : {}} className={pageBtnClass}>{"<<"}</button>
                  <button onClick={() => previousPage()} disabled={!canPreviousPage} style={canPreviousPage ? { color: THEME.primary } : {}} className={pageBtnClass}>{"<"}</button>
                  <span className="px-3 py-1 mx-1 rounded-lg text-white text-sm font-semibold" style={{ backgroundColor: THEME.primary }}>
                    Page {pageIndex + 1} of {pageCount}
                  </span>
                  <button onClick={() => nextPage()} disabled={!canNextPage} style={canNextPage ? { color: THEME.primary } : {}} className={pageBtnClass}>{">"}</button>
                  <button onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage} style={canNextPage ? { color: THEME.primary } : {}} className={pageBtnClass}>{">>"}</button>
                </div>
                <select
                  value={pageSize}
                  onChange={e => setPageSize(Number(e.target.value))}
                  style={{ color: THEME.primary }}
                  className="ml-4 px-2 py-1 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#172C57] focus:border-[#172C57]"
                >
                  {[5, 10, 20, 50, 100].map(size => (
                    <option key={size} value={size}>Show {size}</option>
                  ))}
                </select>
              </div>
            </div>

            <NotInterestedModal
              isOpen={modalIsOpen}
              onRequestClose={closeModal}
              selectedStudent={selectedStudent}
              isUpdate={isUpdate}
              isUpload={isUpload}
              formData={formData}
              setFormData={setFormData}
              selectedCourses={selectedCourses}
              setSelectedCourses={setSelectedCourses}
              dropdownValue={dropdownValue}
              setDropdownValue={setDropdownValue}
              canOnlySelectHigherInterest={canOnlySelectHigherInterest}
              loadingSubmit={loadingSubmit}
              handleChange={handleChange}
              handleSelectChange={handleSelectChange}
              handleRemoveTag={handleRemoveTag}
              handleSubmit={handleSubmit}
              memberType={memberType}
              courses={courses}
              districts={districts}
            />

            <AdvancedSearchModal
              isOpen={searchModalIsOpen}
              onRequestClose={() => setSearchModalIsOpen(false)}
              filteredStudents={filteredStudents}
              searchDistrict={searchDistrict}
              setSearchDistrict={setSearchDistrict}
              searchCity={searchCity}
              setSearchCity={setSearchCity}
              searchCollege={searchCollege}
              setSearchCollege={setSearchCollege}
              setFilteredStudents={setFilteredStudents}
              studentsData={studentsData}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default NotInterestedStudents;